import { StarIcon } from '@heroicons/react/20/solid';

import toReadableLabel from 'src/shared/utils/toReadableLabel';

import { Tag, Typography } from 'src/shared/components/ui';

import MarkdownContent from '../updates-tab/markdown-content/MarkdownContent';
import { CountryFlag, getCountryName } from './CountryFlag';

interface SupplierTabContentProps {
  selectedQuote: any;
}

const RATING_TAG: any = {
  'highly-rated': { color: 'yellow', text: 'Highest Rating' },
  'fastest-lead-time': { color: 'blue', text: 'Fastest' },
  'eco-friendly-materials': { color: 'green', text: 'Eco Materials' },
  'best-value': { color: 'dark_blue', text: 'Best Value' },
  'lowest-moq': { color: 'purple', text: 'Lowest MOQ' },
  'flexible-payment-terms': { color: 'violet', text: 'Flex Payment' }
};

const SupplierTabContent: React.FC<SupplierTabContentProps> = ({
  selectedQuote
}) => {
  return (
    <div className="px-5 flex flex-col gap-5 pb-24">
      <div className="flex gap-1 items-center">
        <Typography variant="h4" weight="bold">
          {selectedQuote.supplier_name}
        </Typography>
      </div>

      {selectedQuote.supplier_factory_country && (
        <div className="flex flex-col">
          <Typography
            variant="body-1"
            weight="medium"
            className="text-secondary-text"
          >
            Origin
          </Typography>
          <div className="flex items-center gap-2">
            <CountryFlag
              countryCode={selectedQuote.supplier_factory_country}
              includeTooltip={false}
            />
            <Typography variant="body-1" weight="medium">
              {getCountryName(selectedQuote.supplier_factory_country)}
            </Typography>
          </div>
        </div>
      )}

      {/* FROM V1 */}
      {selectedQuote.supplier?.average_score && (
        <div className="flex flex-col">
          <Typography
            variant="body-1"
            weight="medium"
            className="text-secondary-text"
          >
            Rating
          </Typography>
          <Typography
            variant="body-1"
            weight="medium"
            className="flex items-center gap-1"
          >
            {selectedQuote.supplier?.average_score}
            {selectedQuote.supplier?.average_score && (
              <StarIcon className="size-4 text-yellow-dark" />
            )}
          </Typography>
        </div>
      )}

      {selectedQuote?.tags?.length > 0 && (
        <div className="flex flex-col">
          <Typography
            variant="body-1"
            weight="medium"
            className="text-secondary-text"
          >
            Reviews
          </Typography>
          <div className="flex gap-2 mt-1">
            {selectedQuote?.tags?.map((tag: string) => {
              const { color, text } = RATING_TAG[tag]
                ? RATING_TAG[tag]
                : { color: 'no_action', text: toReadableLabel(tag) };

              return (
                <Tag color={color} key={tag}>
                  {text}
                </Tag>
              );
            })}
          </div>
        </div>
      )}

      {/* V2 BELOW */}

      <div className="grid grid-cols-2 gap-5">
        {selectedQuote?.supplier_previous_customers && (
          <div className="flex flex-col">
            <Typography
              variant="body-1"
              weight="medium"
              className="text-secondary-text"
            >
              Past client
            </Typography>
            <MarkdownContent
              markdownString={selectedQuote.supplier_previous_customers}
            />
          </div>
        )}

        {selectedQuote?.supplier_certifications && (
          <div className="flex flex-col">
            <Typography
              variant="body-1"
              weight="medium"
              className="text-secondary-text"
            >
              Certificate
            </Typography>

            <MarkdownContent
              markdownString={selectedQuote.supplier_certifications}
            />
          </div>
        )}

        {selectedQuote?.supplier_credentials && (
          <div className="flex flex-col">
            <Typography
              variant="body-1"
              weight="medium"
              className="text-secondary-text"
            >
              Credentials
            </Typography>
            <MarkdownContent
              markdownString={selectedQuote.supplier_credentials}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierTabContent;
